// @ts-nocheck
import { applyTimeZoneOnDate } from "@store/Framework/Common/Utilities/timeZonedDate";
import { getBudget } from "@insite/client-framework/Services/BudgetService";

import translate from "@insite/client-framework/Translate";
import { BudgetModel } from "@insite/client-framework/Types/ApiModels";
import GridItem from "@insite/mobius/GridItem";
import Select from "@insite/mobius/Select";
import Typography from "@store/MobiusOverrides/Typography/Typography";
import useInfinteScroll from "@store/Framework/Common/Hooks/useInfiniteScroll";
import useReportApi, { ApiParameters } from "@store/Framework/Common/Hooks/useReportAPI/useReportAPI";
import { BudgetDetailInvoices } from "@store/Framework/Services/Reporting/ReportingModels";
import { isInvoiceSummaryModel } from "@store/Framework/Services/Reporting/ReportingTypeGuards";
import { CompanyLocation } from "@store/Framework/Store/Bunzl/Pages/Reporting/LoadLocations";
import {
    locationsSelector,
    reportParametersSelector,
} from "@store/Framework/Store/Bunzl/Pages/Reporting/ReportOrderFrequency/ReportOrderFrequencySelectors";
import useClear from "@store/Framework/Store/Bunzl/Pages/Reporting/StoreHooks/useClear";
import { CustomWidgetModule, InvoiceSummaryData } from "@store/Framework/Types/types";
import BudgetDetailCard from "@store/Library/Components/Reporting/BudgetDetailCard/BudgetDetailCard";
import BudgetDetailTable from "@store/Library/Components/Reporting/BudgetDetailCard/BudgetDetailTable";
import AccordionLayout from "@store/Library/Components/Reporting/Common/AccordionLayout";
import ApiButtons from "@store/Library/Components/Reporting/Common/ApiButtons";
import ReportContainer from "@store/Library/Components/Reporting/Common/ReportContainer";
import ReportingFilterGridItem from "@store/Library/Components/Reporting/Common/ReportingFilterGridItem";
import ReportingShipToPicker from "@store/Library/Components/Reporting/ShipToPicker/ReportingShipToPicker";
import { getCustomerIdByNameDictionary, getYears } from "@store/Library/Widgets/Reports/BudgetSummaryReport";
import { getMediaQuery } from "@toolkit/Framework/Common/Utilities/mediaQueries";
import invert from "lodash/invert";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";

const mobileQuery = getMediaQuery({ option: "only", first: true, min: 0, max: 700, last: false });
export interface BudgetDetailsData {
    title: string;
    startDate: string;
    endDate: string;
    limit: number;
    invoices: InvoiceSummaryData[];
}

const CenteredGridItem = styled(GridItem)`
    display: flex;
    justify-content: space-around;
    align-items: center;
    flex-wrap: wrap;
    ${mobileQuery} {
        justify-content: center;
        width: 100%;
        padding: 0;
    }
    row-gap: 2rem;
`;

const HeaderTypography = styled(Typography)`
    text-align: center;
    margin-block: 1rem;
    width: 100%;
    text-align: center;
`;

const getInvoicesByName = (invoices: InvoiceSummaryData[]) => {
    return invoices.reduce((acc, invoice) => {
        acc[invoice.customername] = [...(acc[invoice.customername] || []), invoice];
        return acc;
    }, {} as { [key: string]: InvoiceSummaryData[] });
};
const getBudgetDetailInvoices = (invoiceDetails: InvoiceSummaryData[]): BudgetDetailInvoices[] => {
    return invoiceDetails.map(invoiceDetail => {
        return {
            invoicenom: invoiceDetail.invoicenum,
            expenditures: invoiceDetail.ordertotal,
            invoiceDate: invoiceDetail.invoicedate,
            orderid: invoiceDetail.orderid,
            customerName: invoiceDetail.customername,
        };
    });
};
const getInvoicesBetweenTwoDates = (invoices: InvoiceSummaryData[], startDate: Date, endDate: Date) => {
    return invoices.filter(invoice => {
        const invoiceDate = new Date(invoice.invoicedate);
        return invoiceDate >= startDate && invoiceDate <= endDate;
    });
};

const getBudgetDetailsData = (
    invoices: InvoiceSummaryData[],
    budgetData: BudgetModel[],
    locationData: CompanyLocation[] | undefined,
): BudgetDetailsData[][] => {
    const customerIdByNameDictionary = getCustomerIdByNameDictionary(locationData || []);
    const customerNameByIdDictionary = invert(customerIdByNameDictionary);
    const invoicesByName = getInvoicesByName(invoices);
    return budgetData.map(budget => {
        const shipToId = budget.shipToId;
        const customerName = customerNameByIdDictionary[shipToId];
        const invoices = invoicesByName[customerName] || [];
        if (!budget.budgetLineCollection) {
            return [];
        }
        return budget.budgetLineCollection.map<BudgetDetailsData>((budgetLine, index) => {
            const budgetLimit = budgetLine.currentFiscalYearBudget ?? 0;
            const budgetStartDate = applyTimeZoneOnDate(new Date(budgetLine.startDate));
            let budgetEndDate: Date;
            if (index !== budget.budgetLineCollection!.length - 1) {
                const nextBudgetStartDate = applyTimeZoneOnDate(
                    new Date(budget.budgetLineCollection![index + 1].startDate),
                );

                budgetEndDate = new Date(nextBudgetStartDate.setDate(nextBudgetStartDate.getDate() - 1));
            } else {
                budgetEndDate = applyTimeZoneOnDate(new Date(budgetStartDate.getFullYear(), 12, 0));
            }

            const invoicesBetweenDates = getInvoicesBetweenTwoDates(invoices, budgetStartDate, budgetEndDate);

            const budgetDetailsData = {
                title: customerName,
                startDate: budgetStartDate.toLocaleDateString(),
                endDate: budgetEndDate.toLocaleDateString(),
                limit: budgetLimit,
                invoices: invoicesBetweenDates,
            };
            return budgetDetailsData;
        });
    });
};

const BudgetDetailReport: React.FC = () => {
    const parameters = useSelector(reportParametersSelector);
    const [budgetYear, setBudgetYear] = useState<number>(new Date().getFullYear());
    const [budgets, setBudgets] = useState<BudgetDetailsData[][]>([]);
    const [startInfiniteScroll, setStartInfiniteScroll] = React.useState(false);
    const { itemsToShow, setPage } = useInfinteScroll(2, startInfiniteScroll);
    const onClear = useClear();
    const {
        updateData: updateInvoiceSummaries,
        isLoading,

        isError,
        setHasSubmitted,
    } = useReportApi("invoiceSummary");
    const handleClear = () => {
        onClear();
        setBudgets([]);
        setHasSubmitted(false);
        setPage(1);
        setStartInfiniteScroll(false);
    };
    const locationData = useSelector(locationsSelector);

    const handleDataUpdate = async () => {
        const startOfFiscalYear = new Date(budgetYear, 0, 1).toLocaleDateString();
        const endOfFiscalYear = new Date(budgetYear, 11, 31).toLocaleDateString();
        const customers = parameters.customers;

        const budgetPromises = customers.map(customer =>
            getBudget({ shipToId: customer, fiscalYear: budgetYear, fullGrid: true, userProfileId: "" }),
        );
        const invoiceSummaryPromise = updateInvoiceSummaries({
            ...parameters,
            startDate: startOfFiscalYear,
            endDate: endOfFiscalYear,
        } as ApiParameters);

        const budgetReturns = await Promise.all(budgetPromises);
        const invoiceSummaries = await invoiceSummaryPromise;

        if (isInvoiceSummaryModel(invoiceSummaries)) {
            const budgetDetailsData = getBudgetDetailsData(invoiceSummaries.items, budgetReturns, locationData);
            setBudgets(budgetDetailsData || []);
            setStartInfiniteScroll(true);
            setPage(1);
        }
    };

    return (
        <ReportContainer>
            <AccordionLayout>
                <ReportingFilterGridItem>
                    <ReportingShipToPicker isError={isError} />
                </ReportingFilterGridItem>
                <ReportingFilterGridItem>
                    <Select
                        label={translate("Budget Year")}
                        value={budgetYear}
                        onChange={event => {
                            setBudgetYear(Number(event.currentTarget.value));
                        }}
                    >
                        {getYears().map(year => (
                            <option key={year} value={year}>
                                {year}
                            </option>
                        ))}
                    </Select>
                </ReportingFilterGridItem>
                <ApiButtons onClear={handleClear} updateData={handleDataUpdate} isLoading={isLoading} />
                <CenteredGridItem width={12}>
                    {budgets?.map(
                        (budget, index) =>
                            index < itemsToShow && (
                                <React.Fragment key={budget[0].title}>
                                    <HeaderTypography variant="h2">{budget[0].title}</HeaderTypography>
                                    {budget.map(budget => (
                                        <BudgetDetailTable
                                            key={budget.limit + budget.startDate + budget.title}
                                            budget={budget}
                                            show
                                        />
                                    ))}
                                </React.Fragment>
                            ),
                    )}
                </CenteredGridItem>
            </AccordionLayout>
        </ReportContainer>
    );
};

const widgetModule: CustomWidgetModule = {
    component: BudgetDetailReport,
    definition: {
        group: "Reporting",
        displayName: "Budget Detail Report",
    },
};

export default widgetModule;
